<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="page-header__left">
        <button class="btn-back" @click="$router.push('/landing-pages')">
          <i class="fa fa-arrow-left"></i>
        </button>
        <div>
          <h1 class="page-title">Create Landing Page</h1>
          <span class="page-subtitle">New landing page</span>
        </div>
      </div>
    </div>

    <!-- ── General Panel ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-file-text-o"></i> General</div>

      <div class="form-grid form-grid--3">
        <div class="field-group">
          <label class="field-label">Slug</label>
          <input
            v-model="form.slug"
            class="dd-input"
            placeholder="slug-of-the-landing-page"
          />
        </div>

        <div class="field-group">
          <label class="field-label">Title</label>
          <input
            v-model="form.title"
            class="dd-input"
            placeholder="Title of the landing page"
          />
        </div>

        <div class="field-group">
          <label class="field-label">Menu Title</label>
          <input
            v-model="form.title_menu"
            class="dd-input"
            placeholder="It shows on menu"
          />
        </div>

        <div class="field-group">
          <label class="field-label">Type</label>
          <select v-model="form.type" class="dd-select">
            <option value="">Select type...</option>
            <option value="occasion">Occasion</option>
            <option value="recipient">Recipient</option>
          </select>
        </div>

        <div class="field-group">
          <label class="field-label">Tag</label>
          <select v-model="form.sections.tag" class="dd-select">
            <option value="" disabled>Choose a tag</option>
            <option v-for="tag of tags" :key="tag.id" :value="tag.id">{{ tag.title }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- ── Experiences Panel ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-cube"></i> Experiences</div>

      <div class="product-search-wrap">
        <div class="input-icon-wrap">
          <i class="fa fa-search input-icon"></i>
          <input
            v-model="search"
            class="dd-input dd-input--icon"
            placeholder="Search product title..."
            @keyup="loadExperiences"
          />
        </div>

        <ul class="search-results" v-if="experiences.length > 0">
          <li
            class="search-result-item"
            v-for="experience of experiences"
            :key="experience.id"
            @click="
              addExperience(
                experience.id,
                experience.title,
                experience.thumbnail.url,
                experience.location.city.name,
                experience.categories
              )
            "
          >
            <i class="fa fa-plus-circle"></i>
            <span>{{ experience.title }}</span>
          </li>
        </ul>
      </div>

      <div class="selected-tags">
        <span
          class="product-tag"
          v-for="experience of form.sections.experiences"
          :key="experience.id"
          @click="removeExperience(experience.id)"
        >
          {{ experience.title }}
          <i class="fa fa-times tag-remove"></i>
        </span>
      </div>
    </div>

    <!-- ── Collections Panel ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-th-large"></i> Collections</div>

      <ul class="search-results search-results--scroll" v-if="collections.length > 0">
        <li
          class="search-result-item"
          v-for="collection of collections"
          :key="collection.id"
          @click="addCollection(collection)"
        >
          <i class="fa fa-plus-circle"></i>
          <span>{{ collection.title }}</span>
        </li>
      </ul>

      <div class="selected-tags">
        <span
          class="product-tag"
          v-for="collection of form.sections.collections"
          :key="collection.id"
          @click="removeCollection(collection.id)"
        >
          {{ collection.title }}
          <i class="fa fa-times tag-remove"></i>
        </span>
      </div>
    </div>

    <!-- ── Description Panel ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-align-left"></i> Description</div>
      <vue-editor
        v-model="form.description"
        placeholder="Description"
      ></vue-editor>
    </div>

    <!-- ── Cover Image Panel ── -->
    <div class="panel">
      <div class="panel-title"><i class="fa fa-image"></i> Cover Image</div>
      <div class="field-group field-group--narrow">
        <input
          class="dd-input-file"
          type="file"
          id="formFile"
          @change="uploadPhoto"
          ref="upload"
        />
        <span class="upload-ok" v-if="form.photo">
          <i class="fa fa-check"></i> Photo uploaded!
        </span>
        <img
          v-if="photoPreviewUrl"
          :src="photoPreviewUrl"
          class="photo-preview"
        />
      </div>
    </div>

    <!-- ── Errors ── -->
    <ul class="error-list" v-if="errors && errors.length">
      <li v-for="error in errors" :key="error">{{ error }}</li>
    </ul>

    <!-- ── Actions ── -->
    <div class="actions-bar">
      <label class="toggle-label">
        <input type="checkbox" v-model="form.is_published" class="toggle-input" />
        <span class="toggle-track"><span class="toggle-thumb"></span></span>
        <span class="toggle-text">Published</span>
      </label>
      <div class="actions-bar__buttons">
        <button class="btn-cancel" @click="$router.back()">Cancel</button>
        <button class="btn-submit" @click="submit()">
          <i class="fa fa-check"></i> Create Landing Page
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import { config } from "../../helpers";
import { VueEditor } from "vue2-editor";

export default {
  name: "LandingPageCreate",

  components: {
    VueEditor,
  },

  data() {
    return {
      url: "",
      loaded: false,
      search: '',

      categories: [],
      selectedCategory: null,
      experiences: [],
      experiencesPage: 1,
      experiencesLastPage: 1,

      collections: [],
      tags: [],

      photoPreviewUrl: null,

      form: {
        slug: "",
        title: "",
        title_menu: "",
        type: "",
        description: "",
        photo: null,
        sections: {
          experiences: [],
          collections: [],
          tag: ""
        },
        is_published: false,
      },
      errors: [],
    };
  },

  mounted() {
    this.loadTags();
    this.loadCategories();
    this.loadCollections();
  },

  methods: {
    loadCategories() {
      axios.get(`${config("BACKEND_ROOT")}/categories`).then((response) => {
        this.categories = response.data.data;
        this.loaded = true;
      });
    },

    loadTags() {
      axios.get(`${config("BACKEND_ROOT")}/admin/tags`).then((response) => {
        this.tags = response.data.data;
      });
    },


    loadCollections() {
      axios.get(`${config("BACKEND_ROOT")}/collections`).then((response) => {
        this.collections = response.data.data;
      });
    },

    loadExperiences() {
      const searched = this.search;

      axios
        .get(config("BACKEND_ROOT") + "/admin/products", { params: { 'filters[title]': searched } })
        .then((response) => {
          this.experiences = response.data.data;
          this.experiencesLastPage = response.data.meta.last_page;
        });
    },

    addExperience(id, title, thumbnailUrl, city, categories) {
      if (this.form.sections.experiences.some((el) => el.id == id)) {
        return alert("You've already added this item!");
      }

      this.form.sections.experiences.push({
        id: id,
        title: title,
        thumbnail_url: thumbnailUrl,
        city: city,
        categories: categories.map((category) => {
          return { id: category.id, title: category.title };
        }),
      });
    },

    removeExperience(id) {
      if (confirm("Do you want to remove this item?")) {
        this.form.sections.experiences = this.form.sections.experiences.filter(
          (el) => el.id != id
        );
      }
    },

    addCollection(collection) {
      if (this.form.sections.collections.some((el) => el.id == collection.id)) {
        return alert("You've already added this item!");
      }

      this.form.sections.collections.push({
        id: collection.id,
        title: collection.title,
        thumbnail_url: collection.images[0].url,
        color: collection.color,
        price: collection.price,
      });
    },

    removeCollection(id) {
      if (confirm("Do you want to remove this item?")) {
        this.form.sections.collections = this.form.sections.collections.filter(
          (el) => el.id != id
        );
      }
    },

    submit() {
      axios
        .post(`${config("BACKEND_ROOT")}/admin/landing-pages`, this.form)
        .then((response) => {
          this.$router.push("/landing-pages");
        })
        .catch((error) => {
          this.errors = Object.values(error.response.data.errors).flat();
        });
    },

    uploadPhoto(e) {
      const file = e.target.files[0];
      this.photoPreviewUrl = URL.createObjectURL(file);

      const type = this.$refs.upload.files[0].type.split('/');

      const formData = new FormData();
      formData.append("file", this.$refs.upload.files[0]);
      formData.append("type", type[0]);
      const headers = { "Content-Type": "multipart/form-data" };
      axios
        .post(`${config("BACKEND_ROOT")}/admin/uploads`, formData, { headers })
        .then((res) => {
          this.form.photo = res.data.data.file_name;
        })
        .catch((error) => {
          this.errors = error.response.data.errors.file;
        });
    },
  },
};
</script>

<style scoped>
.page-wrap { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }
.btn-back { width: 34px; height: 34px; margin-top: 4px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: border-color .15s, color .15s; }
.btn-back:hover { border-color: var(--accent); color: var(--accent); }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }

/* ── Panel ── */
.panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.25rem; }
.panel-title { font-size: 12px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 1.25rem; display: flex; align-items: center; gap: 7px; }

/* ── Form Grid ── */
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-grid--3 { grid-template-columns: 1fr 1fr 1fr; }
.field-group { display: flex; flex-direction: column; gap: 6px; }
.field-group--narrow { max-width: 360px; }
.field-label { font-size: 12px; font-weight: 500; color: var(--text-2); }

/* ── Inputs ── */
.dd-input { height: 38px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 12px; outline: none; width: 100%; transition: border-color .15s; box-sizing: border-box; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.dd-select { height: 38px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; cursor: pointer; }
.dd-select:focus { border-color: var(--accent); }
.dd-input-file { width: 100%; padding: 6px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; }
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; pointer-events: none; }
.dd-input--icon { padding-left: 32px; }

/* ── Search / Selection ── */
.product-search-wrap { position: relative; max-width: 480px; }
.search-results { list-style: none; padding: 0; margin: 6px 0 0; background: var(--bg-panel); border: 1px solid var(--border); border-radius: 8px; overflow: hidden; }
.search-results--scroll { max-height: 200px; overflow-y: auto; margin-top: 0; max-width: 480px; }
.search-result-item { display: flex; align-items: center; gap: 8px; padding: 9px 12px; font-size: 13px; color: var(--text-2); cursor: pointer; transition: background .12s; }
.search-result-item:hover { background: var(--bg-hover); color: var(--text-1); }
.search-result-item i { color: var(--accent); font-size: 13px; }
.selected-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 1rem; }
.selected-tags:empty { display: none; }
.product-tag { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 500; background: rgba(99,179,237,.12); color: #63b3ed; border: 1px solid rgba(99,179,237,.25); cursor: pointer; transition: background .12s; }
.product-tag:hover { background: rgba(244,47,75,.12); color: #f42f4b; border-color: rgba(244,47,75,.25); }
.tag-remove { font-size: 10px; }

/* ── Cover image ── */
.upload-ok { font-size: 12px; color: #22c55e; }
.photo-preview { width: 150px; border-radius: 8px; border: 1px solid var(--border); margin-top: 4px; }

/* ── Errors ── */
.error-list { margin: 0; padding-left: 20px; color: var(--accent); font-size: 13px; }

/* ── Toggle ── */
.toggle-label  { display: flex; align-items: center; gap: 10px; cursor: pointer; margin: 0; }
.toggle-input  { display: none; }
.toggle-track  { width: 42px; height: 24px; background: var(--border-2); border-radius: 12px; position: relative; display: block; transition: background .2s; flex-shrink: 0; }
.toggle-input:checked + .toggle-track { background: var(--accent); }
.toggle-thumb  { width: 18px; height: 18px; background: #fff; border-radius: 50%; position: absolute; top: 3px; left: 3px; transition: transform .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.toggle-input:checked + .toggle-track .toggle-thumb { transform: translateX(18px); }
.toggle-text   { font-size: 13px; font-weight: 500; color: var(--text-2); }

/* ── Actions ── */
.actions-bar { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
.actions-bar__buttons { display: flex; gap: 10px; }
.btn-cancel { height: 38px; padding: 0 18px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; transition: border-color .15s; }
.btn-cancel:hover { border-color: var(--border-2); color: var(--text-1); }
.btn-submit { height: 38px; padding: 0 20px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: opacity .15s; }
.btn-submit:hover { opacity: .88; }

@media (max-width: 768px) { .form-grid, .form-grid--3 { grid-template-columns: 1fr; } }
</style>
